import React, { useState } from "react";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import { useTranslation } from "react-i18next";
import { DealData, DealStatus } from "@/pages/deals/types";
import { dealStatusColors } from "@/pages/deals/components/DealsTable/DealsTable";
import { getEnumTKey } from "@/utils/strings";
import { useUsers } from "@/entities/users/hooks";
import { useUserSelect } from "@/entities/users/useUserSelect";
import TopFields, { FieldsConfig } from "@/components/layouts/TopFields";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";
import { useProducts } from "@/entities/settings/hooks";
import { useEntityFields } from "@/pages/settings/hooks";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import EntitySelectField from "@/components/controls/EntitySelectField";
import { get, isEmpty, map } from "lodash";
import UserAvatar from "@/components/controls/UserAvatar";
import { StatusBadge } from "@/components/ui/status-badge";
import DetailsPopover from "@/components/controls/DetailsPopover";
import {
  closedLostReasonsFromIdx,
  getCurrencySymbol
} from "@/pages/deals/helpers";
import { useClosedLostReasons } from "@/entities/settings/deal/hooks";
import { parseClosedLostReasonText } from "@/pages/deals/hooks";
import { ClosedLostDreamerIndication } from "@/pages/deals/components/ClosedLostDreamerIndication";
import DreamerIcons from "@/components/controls/Questions/DreamerIndication/components/DreamerIcons";
import { useAccountSettingByKey } from "@/entities/settings/hooks";
import { useEnablePartnerships } from "@/pages/partners/hooks";
import ArrMrrEditableField from "@/pages/deals/components/commercials/ArrMrrEditableField";
import {
  currencyCodeFromSetting,
  isCurrencyConversionNeeded
} from "@/utils/numbers";
import {
  useContract,
  useDeleteAllRevenueItems,
  useUpdateContract,
  useUpdateDealArr
} from "@/pages/contracts/hooks";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import {
  FINANCIALS_WRITE_PERMISSION,
  useHasPermission
} from "@/services/auth/AuthContext";

interface Props {
  deal?: DealData;
  onUpdateField: (
    field: string,
    newValue: string | number,
    additionalFields?: { [fieldName: string]: string | number }
  ) => void;
  handleOpenCloseDealModal: (
    status: DealStatus.CLOSED_LOST | DealStatus.CLOSED_WON,
    selectedReason?: number | null,
    closedLostReasonText?: string | null
  ) => void;
  className?: string;
  itemClassName?: string;
}

const DEFAULT_CURRENCY_CODE = "USD";

const DealTopFields: React.FC<Props> = ({
  deal,
  onUpdateField,
  handleOpenCloseDealModal,
  className,
  itemClassName
}) => {
  const { t, i18n } = useTranslation();
  const products = useProducts().data?.products ?? [];
  const { fieldName: arrOrMrrField, isArr } = useArrMrr();
  const {
    users: [managedByUser, lastModifiedByUser]
  } = useUsers([
    deal?.managedBy || "",
    deal?.lastModifiedBy || ""
  ]);
  // lastModifiedByUser is used when rendering the "modified by user" Dreamer badge
  const closedLostReasons =
    useClosedLostReasons().data?.closedLostReasons ?? [];

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isChangeProductModalOpen, setIsChangeProductModalOpen] =
    useState(false);
  const [isChangeProductCheckboxChecked, setIsChangeProductCheckboxChecked] =
    useState(false);
  const [pendingProductId, setPendingProductId] = useState<string | null>(null);

  const {
    options: userOptions,
    entities: userEntities,
    isLoading: isLoadingUsers,
    searchTerm: userSearchTerm,
    setSearchTerm: setUserSearchTerm
  } = useUserSelect();

  const { data: dealFields } = useEntityFields(EntityType.DEAL);
  const enablePartnerships = useEnablePartnerships();
  const partnerFieldKey = enablePartnerships
    ? dealFields?.fields?.find(
        (field) => field.displayType === FieldDisplayType.PARTNERS
      )?.fieldKey
    : undefined;
  const partnerContactFieldKey = dealFields?.fields?.find(
    (field) => field.displayType === FieldDisplayType.PARTNER_CONTACTS
  )?.fieldKey;

  const {
    data: contract,
    isLoading: isLoadingContract,
    isError: isContractError
  } = useContract(deal?.contractId ?? undefined);
  const hasRevenueItems = (contract?.revenueItems?.length ?? 0) > 0;
  // Once services exist they drive ARR, so the header only edits it on a deal with none.
  const hasActiveServices = (contract?.revenueItems ?? []).some(
    (item) => !item.isDeleted
  );
  // A closed deal's ARR no longer follows its services, so the lock applies to open deals only.
  const isOpenDeal =
    deal?.status !== DealStatus.CLOSED_WON &&
    deal?.status !== DealStatus.CLOSED_LOST;
  const isArrLockedByServices = isOpenDeal && hasActiveServices;
  // Read-only until the contract loads, so a deal with services never shows an editable field first;
  // a load that failed before any data arrived leaves that unknown, so it stays read-only. A failed
  // refetch keeps the last loaded data, which still answers it. A deal with no contract never queries.
  const isArrReadOnly =
    isOpenDeal &&
    (hasActiveServices || isLoadingContract || (isContractError && !contract));
  const canEditFinancials = useHasPermission(FINANCIALS_WRITE_PERMISSION);
  // Financial fields lock down once the deal is closed won; open deals stay
  // editable by everyone.
  const isFinancialsLocked =
    deal?.status === DealStatus.CLOSED_WON && !canEditFinancials;
  const deleteAllRevenueItems = useDeleteAllRevenueItems();
  const updateContract = useUpdateContract(deal?.contractId ?? undefined);
  const updateDealArr = useUpdateDealArr(deal?.contractId ?? undefined);

  const accountCurrencySetting =
    useAccountSettingByKey("account_currency").data;
  // Null until the setting genuinely resolves. The DEFAULT_CURRENCY_CODE
  // fallback below is for naming and symbol lookup; handing it to the currency
  // comparison would make a deal in the account's own currency read as foreign
  // for one render.
  const accountCurrencyCode = currencyCodeFromSetting(
    accountCurrencySetting?.value
  );
  const contractCurrencyCode = deal?.currency || DEFAULT_CURRENCY_CODE;
  const contractCurrencySymbol = getCurrencySymbol(contractCurrencyCode);
  // Falls back to the code: getCurrencySymbol answers undefined for a currency
  // its package does not carry, and this is interpolated into the converted
  // figure below, which would read "undefined1,234".
  const accountCurrencyForDisplay = accountCurrencyCode ?? DEFAULT_CURRENCY_CODE;
  const accountCurrencySymbol =
    getCurrencySymbol(accountCurrencyForDisplay) || accountCurrencyForDisplay;
  // The same predicate the two currency hooks use. Hand-rolled here before,
  // which is how it kept the resolution gate but missed the case-folding the
  // hooks got — leaving the sign calling `usd` and `USD` one currency while
  // this line called them two, on the same row.
  const showConversion = isCurrencyConversionNeeded(
    accountCurrencyCode,
    deal?.currency
  );

  const getLocalValue = () => {
    return isArr ? (deal?.arrLocal ?? 0) : (deal?.mrrLocal ?? 0);
  };

  const getOriginalValue = () => {
    return isArr ? (deal?.arr ?? 0) : (deal?.mrr ?? 0);
  };

  const handleSaveArrMrrField = (
    fieldName: string,
    newValue: string | number
  ) => {
    if (isFinancialsLocked || isArrReadOnly) return;
    onUpdateField(fieldName, newValue);
    if (
      deal?.id &&
      deal?.contractId &&
      !contract?.isDetachedFromDeal &&
      (fieldName === "arrLocal" || fieldName === "mrrLocal")
    ) {
      const numericValue = typeof newValue === "number" ? newValue : 0;
      updateDealArr.mutate({
        pathParams: { dealId: deal.id },
        data: {
          arrLocal:
            fieldName === "arrLocal" ? numericValue : (deal.arrLocal ?? 0),
          mrrLocal:
            fieldName === "mrrLocal" ? numericValue : (deal.mrrLocal ?? 0)
        }
      });
    }
  };

  const renderArrMrrField = () => {
    const localValue = getLocalValue();
    const originalValue = getOriginalValue();
    const localFieldName = isArr ? "arrLocal" : "mrrLocal";

    return (
      <ArrMrrEditableField
        // The permission lock keeps its own disabled state and tooltip.
        readOnly={isArrReadOnly && !isFinancialsLocked}
        // Said only once services are known to exist, not while they load.
        readOnlyTooltipText={
          isArrLockedByServices
            ? t("deals.dealView.editInTermsTooltip")
            : undefined
        }
        fieldName={localFieldName}
        label={isArr ? t("contractTerms.arr") : t("contractTerms.mrr")}
        value={localValue}
        originalValue={originalValue}
        contractCurrencySymbol={contractCurrencySymbol}
        accountCurrencySymbol={accountCurrencySymbol}
        contractCurrencyCode={deal?.currency}
        accountCurrencyCode={accountCurrencyCode}
        showConversion={showConversion}
        onSave={handleSaveArrMrrField}
        entityId={deal?.id}
        disabled={isFinancialsLocked}
        disabledTooltipText={t("common.noEditPermission")}
      />
    );
  };

  const renderDetailedStatus = (
    status: DealStatus.CLOSED_WON | DealStatus.CLOSED_LOST
  ) => {
    const handleEdit = () => {
      handleOpenCloseDealModal(
        status,
        deal?.closedLostReason,
        deal?.closedLostReasonText
      );
    };

    const getStatusReasonProps = () => {
      if (!closedLostReasons?.length) {
        return {};
      }
      const reason = closedLostReasonsFromIdx(
        t,
        i18n,
        closedLostReasons,
        deal?.closedLostReason as number
      );
      if (!isEmpty(reason)) {
        const { analysis } = parseClosedLostReasonText(
          deal?.closedLostReasonText
        );

        // User changed the reason = current reason differs from what Dreamer suggested.
        // Changing only the note (same reason) still counts as "Generated by Dreamer".
        const isDreamerModifiedByUser =
          (analysis?.suggestedByDreamer ?? false) &&
          analysis?.suggestedReasonId != null &&
          deal?.closedLostReason !== analysis.suggestedReasonId;

        const handleRestoreDreamerSuggestion = isDreamerModifiedByUser
          ? () => {
              // Pass reconstructed JSON with userNote cleared so Dreamer provenance
              // is preserved on save, while the user's overriding note is discarded.
              const restoredAnalysis: import("@/pages/deals/hooks").ClosedLostReasonAnalysis = {
                suggestedByDreamer: true,
                justification: analysis.justification,
                citation: analysis.citation,
                userNote: null,
                suggestedReasonId: analysis.suggestedReasonId ?? null
              };
              handleOpenCloseDealModal(
                status,
                analysis.suggestedReasonId,
                JSON.stringify(restoredAnalysis)
              );
            }
          : undefined;

        let dreamerBadge: React.ReactNode = null;
        if (analysis?.suggestedByDreamer && analysis.justification) {
          dreamerBadge = (
            <ClosedLostDreamerIndication
              justification={analysis.justification}
              citation={analysis.citation}
              filledAt={deal?.closedAt}
              size="sm"
              modifiedByUserData={isDreamerModifiedByUser ? lastModifiedByUser : null}
              onRestore={handleRestoreDreamerSuggestion}
              showChip
            />
          );
        } else if (analysis?.suggestedByDreamer) {
          // Justification absent — show non-interactive icon only
          dreamerBadge = (
            <DreamerIcons
              dreamerMode
              isLastEditedByDreamer
              lastEditedByData={undefined}
              previousLastEditedBy={null}
              size="sm"
            />
          );
        }

        // For Dreamer deals show only the user's note (justification surfaces in dreamerBadge).
        // For plain-text deals show the raw closedLostReasonText.
        const statusNotes = analysis
          ? (analysis.userNote || null)
          : (deal?.closedLostReasonText || null);

        // When Dreamer set the reason and user hasn't overridden it, attribute to Dreamer
        const setByLabel =
          analysis?.suggestedByDreamer && !isDreamerModifiedByUser
            ? t("deals.dealView.filledByDreamer.filledByDreamer")
            : null;

        return {
          statusReasonTitle: t("deals.closedLostReasonName"),
          statusReasonText: reason,
          statusNotesTitle: t("deals.modal.comment"),
          statusNotes,
          dreamerBadge,
          setByLabel
        };
      }
    };
    return (
      <div className="flex gap-[2px] items-center pr-2">
        <StatusBadge
          statusColor={dealStatusColors[deal?.status || DealStatus.IN_PROGRESS]}
          className="border-none text-base"
        >
          {t(
            getEnumTKey(
              "stakeholders.dealsModal.table",
              "status",
              DealStatus,
              deal?.status as number
            )
          )}
        </StatusBadge>
        <DetailsPopover
          isOpen={isPopoverOpen}
          setIsOpen={setIsPopoverOpen}
          onEdit={handleEdit}
          statusChangedAt={deal?.closedAt}
          changedByUser={managedByUser}
          {...getStatusReasonProps()}
        />
      </div>
    );
  };

  const {
    options: companyOptions,
    onSearch: onCompanyOptionsSearch,
    isLoading: isLoadingCompanies,
    searchTerm: companySearchTerm
  } = useEntitySelect({
    entityType: EntityType.COMPANY,
    selectedId: deal?.companyId || "",
    alwaysEnabled: true
  });

  const handleUpdateCompanyField = (companyId: string) => {
    onUpdateField("companyId", companyId);
    if (deal?.contractId) {
      updateContract.mutate({
        pathParams: { contractId: deal.contractId },
        data: { companyId }
      });
    }
  };

  const currentProductName =
    products.find((p) => p.id === deal?.productId)?.name ?? "";

  const handleUpdateProductField = (productId: string) => {
    // A product change deletes/replaces the contract's service lines, so it
    // falls under the financials lock.
    if (isFinancialsLocked) return;
    if (hasRevenueItems) {
      setPendingProductId(productId);
      setIsChangeProductCheckboxChecked(false);
      setIsChangeProductModalOpen(true);
    } else {
      onUpdateField("productId", productId, {
        licenseId: null as unknown as string
      });
      if (deal?.contractId) {
        updateContract.mutate({
          pathParams: { contractId: deal.contractId },
          data: { productId }
        });
      }
    }
  };

  const handleConfirmProductChange = () => {
    if (isFinancialsLocked) return;
    if (pendingProductId && deal?.contractId) {
      deleteAllRevenueItems.mutate(
        { pathParams: { contractId: deal.contractId } },
        {
          onSuccess: () => {
            onUpdateField("productId", pendingProductId, {
              licenseId: null as unknown as string
            });
            updateContract.mutate({
              pathParams: { contractId: deal.contractId! },
              data: { productId: pendingProductId }
            });
            setIsChangeProductModalOpen(false);
            setPendingProductId(null);
            setIsChangeProductCheckboxChecked(false);
          },
          onError: () => {
            setIsChangeProductModalOpen(false);
            setPendingProductId(null);
            setIsChangeProductCheckboxChecked(false);
          }
        }
      );
    } else if (pendingProductId) {
      onUpdateField("productId", pendingProductId, {
        licenseId: null as unknown as string
      });
      if (deal?.contractId) {
        updateContract.mutate({
          pathParams: { contractId: deal.contractId },
          data: { productId: pendingProductId }
        });
      }
      setIsChangeProductModalOpen(false);
      setPendingProductId(null);
      setIsChangeProductCheckboxChecked(false);
    }
  };

  const handleCancelProductChange = () => {
    setIsChangeProductModalOpen(false);
    setPendingProductId(null);
    setIsChangeProductCheckboxChecked(false);
  };

  const pendingProductName =
    products.find((p) => p.id === pendingProductId)?.name ?? "";

  const fieldsConfig: FieldsConfig = {
    companyId: {
      type: "select",
      icon: "company",
      entity: EntityType.COMPANY,
      options: companyOptions,
      searchTerm: companySearchTerm,
      showSearch: true,
      onSearch: onCompanyOptionsSearch,
      isLoading: isLoadingCompanies,
      popoverClassName: "max-w-[30vw]",
      onSelect: handleUpdateCompanyField
    },
    productId: {
      type: "select",
      options: map(products, (product) => ({
        id: product.id,
        displayName: product.name
      })),
      icon: "product",
      onSelect: handleUpdateProductField,
      // Product changes replace the contract's service lines, so the selector
      // locks with the rest of the financials.
      disabled: isFinancialsLocked,
      disabledTooltipText: t("common.noEditPermission")
    },
    managedBy: {
      type: "select",
      entity: EntityType.USER,
      options: userOptions,
      showEmptyIcon: true,
      showSearch: true,
      searchTerm: userSearchTerm,
      onSearch: setUserSearchTerm,
      isLoading: isLoadingUsers,
      renderOptionIcon: (value) => {
        const user =
          userEntities.find((us) => us.id === (value as string)) ??
          (managedByUser?.id === (value as string) ? managedByUser : undefined);
        return <UserAvatar userData={user} size="S" className="mr-1" />;
      }
    },
    ...(partnerFieldKey
      ? {
          [partnerFieldKey]: {
            type: "nonEditable" as const,
            icon: "partner" as const,
            customComponent: (
              <EntitySelectField
                fieldName={partnerFieldKey}
                displayType={FieldDisplayType.PARTNERS}
                value={(get(deal, partnerFieldKey) as string) ?? null}
                onChange={(newValue) =>
                  onUpdateField(
                    partnerFieldKey,
                    newValue as string,
                    partnerContactFieldKey
                      ? { [partnerContactFieldKey]: null as unknown as string }
                      : undefined
                  )
                }
              />
            )
          }
        }
      : {}),
    status: {
      type: "nonEditable",
      customComponent:
        deal?.status === DealStatus.CLOSED_LOST ? (
          renderDetailedStatus(deal?.status as DealStatus.CLOSED_LOST)
        ) : (
          <StatusBadge
            statusColor={
              dealStatusColors[deal?.status || DealStatus.IN_PROGRESS]
            }
            className="border-none text-base"
          >
            {t(
              getEnumTKey(
                "stakeholders.dealsModal.table",
                "status",
                DealStatus,
                deal?.status as number
              )
            )}
          </StatusBadge>
        )
    },
    [arrOrMrrField]: {
      type: "nonEditable",
      icon: "arrMrr",
      customComponent: renderArrMrrField()
    },
    expectedCloseAt: {
      type: "date",
      icon: "expectedCloseAt",
      // Required only on create; the column is nullable and undated deals exist.
      clearable: true
    }
  };

  return (
    <>
      <TopFields
        entity={deal}
        fieldsConfig={fieldsConfig}
        onUpdateField={onUpdateField}
        className={className}
        itemClassName={itemClassName}
      />
      <ConfirmationModal
        isOpen={isChangeProductModalOpen}
        setIsOpen={setIsChangeProductModalOpen}
        title={t("deals.dealView.changeProductModal.title", {
          newProductName: pendingProductName
        })}
        confirmationText={t("deals.dealView.changeProductModal.description", {
          currentProductName,
          newProductName: pendingProductName
        })}
        handleSubmit={handleConfirmProductChange}
        handleCancel={handleCancelProductChange}
        isPrimaryButton={false}
        submitButtonText={t("deals.dealView.changeProductModal.submitButton")}
        cancelButtonText={t("cancel")}
        checkboxLabel={t("deals.dealView.changeProductModal.checkboxLabel")}
        isCheckboxRequired={true}
        onCheckboxChange={setIsChangeProductCheckboxChecked}
        isCheckboxChecked={isChangeProductCheckboxChecked}
        data-test="change-product-confirmation-modal"
      />
    </>
  );
};

export default DealTopFields;
